import React, { useCallback, useEffect, useMemo } from "react";
import {
  CalendarCheck,
  ListChecks,
  MagnifyingGlass,
  PaintBrushHousehold,
  X,
} from "phosphor-react";
import styled from "styled-components";

// API
import api from "../../api/api";

// Context
import {
  ACTIONS as MODAL_ACTIONS,
  useModalHistory,
} from "../../context/ModalHistoryProvider";
import {
  ACTIONS as DEAL_ACTIONS,
  useDealsContext,
} from "../../context/DealsProvider";

// Components
import { AddNewDealsButton } from "../Controls/AddNewDealsButton";
import { CancelButton } from "../Controls/CancelButton";
import { ConfirmButton } from "../Controls/ConfirmButton";
import InputBase from "../Inputs/InputBase";
import SelectMulti from "../Inputs/SelectMulti";
import AddNewDealsModal from "../Modals/AddNewDeals/AddNewDealsModal";
import QueueStyle from "../Styled/Queue.style";

// Utils
import { inputWithStyle } from "../../HOC/inputWithStyle";
import { inputWithFloatingLabel } from "../../HOC/inputWithFloatingLabel";
import colors from "../../utils/constants/COLORS";
import useAsync from "../../utils/hooks/useAsync";
import useArray from "../../utils/hooks/useArray";
import useType from "../../utils/hooks/useType";
import { CONDITIONS, NEW_CARS_ALLOWED_USERS, USER_KEY } from "../../utils/constants/SYSTEM";
import { inputWithHelpers } from "../../HOC/inputWithHelpers";
import Loading from "../Loading/Loading";
import DateRange from "../DateRange/DateRange";
import { IconButton, Tooltip } from "@mui/material";
import StackStyle from "../Styled/Stack.style";
import { useRef } from "react";
import Popup from "../Popup/Popup";
import ConditionSelect from "../Controls/ConditionSelect";
import inputValidators from "../../utils/helpers/inputValidators";
import ExcelFileUploadHistoryModal from "../Modals/ExcelFileUploadHistory/ExcelFileUploadHistoryModal";
import { get } from "../../utils/helpers/storage";

export default function MainTableControls() {
  const user = get(USER_KEY);
  const isNewCarsAllowedUser = NEW_CARS_ALLOWED_USERS.includes(user?.emailAddress?.toLowerCase());
  const searchByDateRangeRef = useRef();

  const [keywords, setKeywords] = useType("string", "");
  const [condition, setCondition] = useType("string", "");
  const [showDateRangeSearch, setShowDateRangeSearch] = useType(
    "boolean",
    false
  );
  const [dateRange, setDateRange] = useType("array", []);
  const [isIncludeInactive, setIsIncludeInactive] = useType("boolean", false);
  const [isIncludeElapsed, setIsIncludeElapsed] = useType("boolean", false);
  const { NEW, DEMO, USED } = CONDITIONS;
  const newDemoUsed = condition === NEW ? 1 : condition === DEMO ? 2 : 3;
  const [isSearchInputFocused, setIsSearchInputFocused] = useType(
    "boolean",
    false
  );
  const [isAllSelected, setIsAllSelected] = useType(
    "boolean",
    condition === NEW ? true : false
  );
  const [mayProceed, setMayProceed] = useType("boolean", false);

  const {
    array: brandOptions,
    setArray: setBrandOptions,
    update: updateBrandOptions,
    updateAll: updateAllBrandOptions,
  } = useArray([]);

  const [fetchBrands, brandsResult, isFetchingBrands] = useAsync(
    useCallback(
      () => condition === NEW && api.getBrands(true),
      [NEW, condition]
    ),
    "",
    "Issue fetching brands."
  );

  const [fetchDeals, dealsResult, isFetchingDeals] = useAsync(
    useCallback(
      () =>
        condition === NEW
          ? api.getDealsByBrandID(brandOptions, isIncludeInactive, isIncludeElapsed)
          : api.getAllActiveUsedDeals(isIncludeInactive),
      [NEW, condition, isIncludeInactive, isIncludeElapsed, brandOptions]
    ),
    "",
    "Issue fetching Deals."
  );

  const [searchKeywords, searchKeywordsResult, isSearchingKeywords] = useAsync(
    useCallback(
      () => {
        if (keywords.length === 0) return;

        const keywordsSplit = keywords.split(" ").slice(0, 4);
        const queryMap = keywordsSplit.map((k, index) => `keyword${++index}=${k}`);
        console.log('queryMap', queryMap);
        const query = queryMap.join("&");
        console.log('query', query);

        return newDemoUsed === 1 ? api.searchKeywordNew(query) : api.searchKeywordUsed(query)
      },
      [keywords, newDemoUsed]
    ),
    "",
    "Issue fetching brands by keyword."
  );

  const [
    searchDateRange,
    searchDateRangeResult,
    isSearchingDateRange,
    ,
    clearSearchDateRange,
  ] = useAsync(
    useCallback(
      () =>
        condition === NEW
          ? api.searchDateRangeNew(
              dateRange[0],
              dateRange[1],
              isIncludeInactive
            )
          : api.searchDateRangeUsed(
              dateRange[0],
              dateRange[1],
              isIncludeInactive
            ),
      [NEW, condition, dateRange, isIncludeInactive]
    ),
    "",
    "Issue fetching brands by date range."
  );

  const [{ refetchDeals }, dispatchDeals] = useDealsContext();
  const [, dispatchModalHistory] = useModalHistory();
  const pushEnd = (modal) =>
    dispatchModalHistory({
      type: MODAL_ACTIONS.PUSH_END,
      payload: { value: modal },
    });

  useEffect(() => {
    dispatchDeals({ type: DEAL_ACTIONS.SET_CONDITION, payload: condition });
  }, [condition, dispatchDeals]);

  // Set brand options for dropdown
  useEffect(() => {
    if (
      brandsResult === undefined ||
      brandsResult === null ||
      brandsResult?.length === 0
    )
      return;
    setBrandOptions(
      brandsResult?.map((b) => ({
        brand: b.brand,
        brandID: b.brand_ID,
        selected: isAllSelected,
      }))
    );
  }, [brandsResult, isAllSelected, setBrandOptions, fetchDeals]);

  // Fetch deals based on brands selected
  useEffect(() => {
    if (
      brandOptions === null ||
      brandOptions === undefined ||
      brandOptions?.length === 0 ||
      brandOptions.every(b => !b?.selected)
    )
      return;

    fetchDeals(true);
  }, [brandOptions, isIncludeInactive, isIncludeElapsed, fetchDeals]);

  // Refetch deals
  useEffect(() => {
    if (refetchDeals) {
      fetchDeals(true);
      dispatchDeals({ type: DEAL_ACTIONS.REFETCH_DEALS, payload: false });
    }
  }, [refetchDeals, fetchDeals, dispatchDeals]);

  // Fetch deals if condition is USED
  useEffect(() => {
    if (condition === USED) fetchDeals(true);
  }, [condition, isIncludeInactive, USED, fetchDeals]);

  // Enter key event listener (Get deals based on search keywords)
  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        if (isSearchInputFocused) searchKeywords(true);
      }
    };
    document.addEventListener("keydown", listener);

    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [isSearchInputFocused, searchKeywords]);

  // Pass deals to context
  useEffect(() => {
    dispatchDeals({
      type: DEAL_ACTIONS.SET_DEALS,
      payload:
        keywords?.length > 0 && searchKeywordsResult?.length > 0
          ? searchKeywordsResult
          : searchDateRangeResult?.length > 0
          ? searchDateRangeResult
          : dealsResult || [],
    });
  }, [
    keywords,
    brandOptions,
    searchKeywordsResult,
    searchDateRangeResult,
    dealsResult,
    dispatchDeals,
  ]);

  // Handle brand selected
  const handleBrandOptionChange = (value) => {
    brandOptions.forEach((b, index) => {
      if (b.brand === value.brand)
        updateBrandOptions(index, {
          ...b,
          selected: !b.selected,
        });
    });
  };

  // Handle trimming
  const brandOptionNames = () => {
    const temp = brandOptions?.filter((b) => b.selected).map((b) => b.brand);
    if (temp.length > 2) {
      temp.splice(2);
      temp.push("...");
    }

    return temp.join(", ");
  };

  // Handle showing loading skeleton on table
  useEffect(() => {
    if (
      isSearchingDateRange ||
      (condition === NEW && isFetchingBrands) ||
      isFetchingDeals ||
      isSearchingKeywords
    ) {
      dispatchDeals({ type: DEAL_ACTIONS.SET_IS_LOADING, payload: true });
    } else {
      dispatchDeals({ type: DEAL_ACTIONS.SET_IS_LOADING, payload: false });
    }
  }, [
    isSearchingDateRange,
    isFetchingBrands,
    isFetchingDeals,
    isSearchingKeywords,
    condition,
    NEW,
    dispatchDeals,
  ]);

  const requiredFields = useMemo(
    () => ({
      startDate: {
        value: dateRange[0],
        validate: (option) =>
          !dateRange[0] && !option
            ? "Please fill out this field"
            : inputValidators.date(option || dateRange[0]),
      },
      endDate: {
        value: dateRange[1],
        validate: (option) =>
          !dateRange[1] && !option
            ? "Please fill out this field"
            : inputValidators.date(option || dateRange[1]),
      },
    }),
    [dateRange]
  );

  useEffect(() => {
    if (
      Object.values(requiredFields).filter(
        (field) => field.validate().length > 0
      ).length > 0
    ) {
      setMayProceed(false);
    } else {
      setMayProceed(true);
    }
  }, [requiredFields, setMayProceed]);

  return (
    <Container>
      <QueueStyle gap="10px">
        <StackStyle width="min-content">
          <QueueStyle width="max-content" gap="10px">
            <ConditionSelect
              options={isNewCarsAllowedUser ? [NEW, USED] : [USED]}
              defaultValue={"Select a condition"}
              handleChange={(value) => {
                setCondition(value);
                fetchBrands(true);
              }}
              value={condition}
              isIncludeInactive={isIncludeInactive}
              setIsIncludeInactive={setIsIncludeInactive}
              isIncludeElapsed={isIncludeElapsed}
              setIsIncludeElapsed={setIsIncludeElapsed}
              prefix={
                <PaintBrushHousehold
                  size={14}
                  weight="thin"
                  color={colors.secondary}
                />
              }
              label="Condition*"
              id="condition"
              error={""}
              width="max-content"
            />

            {condition === NEW && (
              <SelectMulti
                options={brandOptions}
                defaultValue={`Select a brand (${brandsResult?.length || 0})`}
                handleChange={(value) => {
                  handleBrandOptionChange(value);
                }}
                isAllSelected={isAllSelected}
                setIsAllSelected={setIsAllSelected}
                updateAll={updateAllBrandOptions}
                value={brandOptionNames()}
                prefix={
                  isFetchingBrands || isFetchingDeals ? (
                    <Loading />
                  ) : (
                    <ListChecks
                      size={14}
                      weight="thin"
                      color={colors.secondary}
                    />
                  )
                }
                label="Brand"
                id="brand"
                error={() => ""}
              />
            )}

            {condition && (
              <>
                <SearchInput
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  onFocus={() => setIsSearchInputFocused(true)}
                  onBlur={() => setIsSearchInputFocused(false)}
                  label="Search"
                  id="search"
                  helpers={[
                    isSearchingKeywords ? (
                      <Loading />
                    ) : (
                      <MagnifyingGlass
                        size={16}
                        weight="thin"
                        color={colors.secondary}
                        onClick={() => {
                          searchKeywords(true);
                        }}
                      />
                    ),
                    <X
                      weight="thin"
                      size={14}
                      onClick={() => {
                        setKeywords("");
                      }}
                    />,
                  ]}
                />
                <Tooltip
                  title="Search by date range"
                  ref={searchByDateRangeRef}
                >
                  <IconButton
                    onClick={() => setShowDateRangeSearch(!showDateRangeSearch)}
                  >
                    <CalendarCheck weight="thin" size={22} />
                  </IconButton>
                </Tooltip>
                <Popup
                  open={showDateRangeSearch}
                  anchor={searchByDateRangeRef.current}
                  title={
                    <QueueStyle>
                      Select a start and end date{" "}
                      <Tooltip title="Clear dates">
                        <IconButton
                          onClick={() => {
                            setDateRange([".", "."]);
                            clearSearchDateRange([]);
                          }}
                        >
                          <X weight="thin" size={18} />
                        </IconButton>
                      </Tooltip>
                    </QueueStyle>
                  }
                  body={
                    <DateRange
                      dates={dateRange}
                      setDates={setDateRange}
                      validations={{
                        startDate: requiredFields.startDate.validate,
                        endDate: requiredFields.endDate.validate,
                      }}
                    />
                  }
                  action={
                    <QueueStyle>
                      <CancelButton
                        onClick={() => setShowDateRangeSearch(false)}
                      />
                      <ConfirmButton
                        disabled={!mayProceed}
                        onClick={() => searchDateRange(true)}
                      />
                    </QueueStyle>
                  }
                  variant="info"
                  onClose={() => setShowDateRangeSearch(false)}
                />
              </>
            )}
          </QueueStyle>
        </StackStyle>
        <QueueStyle gap="5px" width="max-content">
          <Tooltip title="Excel file upload history">
            <IconButton onClick={() => pushEnd(<ExcelFileUploadHistoryModal />)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 256 256">
                <path fill="currentColor" d="M200 28H72a12 12 0 0 0-12 12v28H40a12 12 0 0 0-12 12v96a12 12 0 0 0 12 12h20v28a12 12 0 0 0 12 12h128a12 12 0 0 0 12-12V40a12 12 0 0 0-12-12m-44 72h48v56h-48Zm48-60v52h-48V80a12 12 0 0 0-12-12h-4V36h60a4 4 0 0 1 4 4M68 40a4 4 0 0 1 4-4h60v32H68ZM36 176V80a4 4 0 0 1 4-4h104a4 4 0 0 1 4 4v96a4 4 0 0 1-4 4H40a4 4 0 0 1-4-4m32 40v-28h64v32H72a4 4 0 0 1-4-4m132 4h-60v-32h4a12 12 0 0 0 12-12v-12h48v52a4 4 0 0 1-4 4M68.93 149.44L86.79 128l-17.86-21.44a4 4 0 0 1 6.14-5.12L92 121.75l16.93-20.31a4 4 0 1 1 6.14 5.12L97.21 128l17.86 21.44a4 4 0 1 1-6.14 5.12L92 134.25l-16.93 20.31A4 4 0 0 1 72 156a3.94 3.94 0 0 1-2.56-.93a4 4 0 0 1-.51-5.63" />
              </svg>
            </IconButton>
          </Tooltip>
          <AddNewDealsButton
            onClick={() => {
              pushEnd(<AddNewDealsModal />);
            }}
          />
        </QueueStyle>
      </QueueStyle>
    </Container>
  );
}

const Container = styled.div`
  background-color: #fff;
  padding: 1rem;
  border-top: thin solid lightgrey;
  border-bottom: thin solid lightgrey;
`;

const SearchInput = inputWithStyle(
  inputWithHelpers(inputWithFloatingLabel(InputBase))
);
